/**
 * <TimezonePicker> — choisir UN fuseau dans la liste (organism).
 *
 * Un champ de recherche, puis les fuseaux qui correspondent, en lignes de réglage dans une seule
 * carte : la ville d'abord, la région en valeur, une coche sur le fuseau en cours. Toucher une
 * ligne la choisit ; pendant l'envoi, sa valeur laisse place au rond d'attente de `<SettingRow>`.
 *
 * La liste est bornée (`searchTimezoneOptions`) : quatre cents fuseaux se cherchent, ils ne se
 * parcourent pas. Au-delà de la borne, une ligne invite à préciser la recherche.
 *
 * Contrôlé pour le métier (les fuseaux, le choix en cours, l'envoi, l'erreur arrivent en props) ;
 * la saisie, elle, reste ici — c'est de l'état d'interface. `<Autocomplete>` ne convient pas : il
 * coche PLUSIEURS éléments et dessine une carte par option.
 */
import React, { useMemo, useState } from "react"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Banner } from "../../atoms/Banner"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { CardList, CardListRow } from "../../molecules/CardList"
import { SettingRow } from "../../molecules/SettingRow"
import { searchTimezoneOptions } from "../../../ports/primary/ui/formatters/timezoneFormatter"
import { type UITimezoneOption } from "../../../ports/primary/ui/types"
import { TimezonePickerSkeleton } from "./TimezonePickerSkeleton"

export type TimezonePickerProps = {
    options: UITimezoneOption[]
    /** Le fuseau choisi — sa ligne porte la coche. */
    selectedId?: string | null
    /** Le fuseau en cours d'envoi — sa ligne attend, les autres ne répondent plus. */
    savingId?: string | null
    onSelect: (id: string) => void
    /** La liste arrive : sa forme à la place des lignes. */
    loading?: boolean
    /** La liste n'a pas pu arriver — déjà écrit. */
    error?: string | null
    onRetry?: () => void
    /** La saisie au premier rendu — pour les stories ; à l'écran, c'est la personne qui tape. */
    defaultQuery?: string
    testID?: string
}

export function TimezonePicker(props: TimezonePickerProps) {
    const {
        options,
        selectedId,
        savingId,
        onSelect,
        loading = false,
        error,
        onRetry,
        defaultQuery = "",
        testID,
    } = props

    const [query, setQuery] = useState(defaultQuery)
    const { shown, hidden } = useMemo(() => searchTimezoneOptions(options, query), [options, query])
    const busy = savingId != null

    let list: React.ReactNode
    if (loading) {
        list = (
            <DelayedSkeleton>
                <TimezonePickerSkeleton />
            </DelayedSkeleton>
        )
    } else if (error != null && error !== "") {
        list = (
            <Stack gap="3" align="start">
                <Banner message={error} color="danger" icon="exclamation-circle" align="start" tinted />
                {onRetry != null && <Button label="Réessayer" onPress={onRetry} color="primary" size="sm" />}
            </Stack>
        )
    } else if (shown.length === 0) {
        list = (
            <Text size="text-md" color="text-muted">
                {`Aucun fuseau ne correspond à « ${query.trim()} ».`}
            </Text>
        )
    } else {
        list = (
            <CardList>
                {shown.map((option) => (
                    <CardListRow key={option.id} py="1">
                        <SettingRow
                            iconName={option.id === selectedId ? "check-circle" : "globe"}
                            label={option.city}
                            value={option.region || undefined}
                            selected={option.id === selectedId}
                            chevron={false}
                            loading={option.id === savingId}
                            onPress={busy ? undefined : () => onSelect(option.id)}
                            testID={`timezone-${option.id}`}
                        />
                    </CardListRow>
                ))}
                {hidden > 0 && (
                    <CardListRow>
                        <Text size="text-sm" color="text-muted">
                            {`Encore ${hidden} fuseau${hidden > 1 ? "x" : ""} : à trouver en précisant la recherche.`}
                        </Text>
                    </CardListRow>
                )}
            </CardList>
        )
    }

    return (
        <Stack gap="3" testID={testID} style={styles.root}>
            <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Rechercher une ville"
                accessibilityLabel="Rechercher un fuseau"
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="search"
                disabled={loading}
                left={<Icon name="search" size={16} color="text-muted" />}
            />
            {list}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    root: { width: "100%" },
}))
